// libraries.
import { useEffect } from "react";
import { useForm } from "react-hook-form";

// interfaces.
import { IncentiveStep } from "../../rulesData";
import {
  RawRule,
  RawRuleItem,
  RawConditionComplex,
} from "@store/apiSlices/incentiveRules/interfaces/rawRules.interfaces";

// store.
import { useRulesSlice } from "@store/apiSlices/incentiveRules/slices/rules";

// ─── Form values ──────────────────────────────────────────────────────────────

export interface EditStepFormValues {
  name: string;
  description: string;
  priority: number;
  isActive: boolean;
  // Day range (only present when rule has dias_full conditions)
  dayRangeFrom?: number;
  dayRangeTo?: number | "";
  // Percentage (only present when rule has descuentos/api_bonos actions)
  percentage?: number;
}

// ─── Helpers — extract ────────────────────────────────────────────────────────

const isComplexCondition = (
  condition: RawRule["condition"],
): condition is RawConditionComplex => "conditions" in condition;

/**
 * Returns the day range [from, to_inclusive] from the raw rule conditions.
 * Returns null if the rule doesn't have dias_full conditions (e.g. preventa).
 */
const extractDayRange = (
  rawRule: RawRuleItem,
): { from: number; to: number | null } | null => {
  const { condition } = rawRule.rule;
  if (!isComplexCondition(condition)) return null;

  const diasConditions = condition.conditions.filter(
    (c) => typeof c.left === "string" && c.left.includes("dias_full"),
  );
  if (diasConditions.length === 0) return null;

  const fromCond = diasConditions.find((c) => c.type === "greater_or_equal");
  const toCond = diasConditions.find((c) => c.type === "less_than");

  const from = fromCond ? (fromCond.right as number) : 0;
  // Show as inclusive last day (less_than 210 → show 209)
  const to = toCond ? (toCond.right as number) - 1 : null;

  return { from, to };
};

/**
 * Returns the percentage (as display value 0-100) from the first
 * descuentos or api_bonos action. Returns null if none found.
 */
const extractPercentage = (rawRule: RawRuleItem): number | null => {
  const action = rawRule.rule.actions.find(
    (a) =>
      (a.tool === "descuentos" || a.tool === "api_bonos") &&
      a.params.percentage !== undefined,
  );
  if (!action) return null;
  return (action.params.percentage as number) * 100;
};

// ─── Helpers — inject back ────────────────────────────────────────────────────

/**
 * Updates all dias_full conditions in the rule with the new range values.
 */
const injectDayRange = (
  rule: RawRule,
  from: number,
  toInclusive: number | null,
): RawRule => {
  if (!isComplexCondition(rule.condition)) return rule;

  // If no upper limit, remove less_than condition; otherwise set it as exclusive (to + 1)
  const updatedConditions = rule.condition.conditions
    .filter((c) => {
      if (
        typeof c.left === "string" &&
        c.left.includes("dias_full") &&
        c.type === "less_than" &&
        toInclusive === null
      )
        return false;
      return true;
    })
    .map((c) => {
      if (typeof c.left !== "string" || !c.left.includes("dias_full"))
        return c;
      if (c.type === "greater_or_equal") return { ...c, right: from };
      if (c.type === "less_than" && toInclusive !== null)
        return { ...c, right: toInclusive + 1 };
      return c;
    });

  return {
    ...rule,
    condition: { ...rule.condition, conditions: updatedConditions },
  };
};

/**
 * Updates the percentage param in all descuentos/api_bonos actions.
 */
const injectPercentage = (rule: RawRule, percentageDisplay: number): RawRule => {
  const pct = percentageDisplay / 100;
  return {
    ...rule,
    actions: rule.actions.map((a) => {
      if (
        (a.tool === "descuentos" || a.tool === "api_bonos") &&
        a.params.percentage !== undefined
      )
        return { ...a, params: { ...a.params, percentage: pct } };
      return a;
    }),
  };
};

// ─── Hook ─────────────────────────────────────────────────────────────────────

interface UseEditStepFormProps {
  step: IncentiveStep | null;
  rawRule: RawRuleItem | null;
  country: string;
  onClose: () => void;
}

export const useEditStepForm = ({
  step,
  rawRule,
  country,
  onClose,
}: UseEditStepFormProps) => {
  const { saveIncentiveStep, isSaving } = useRulesSlice();
  const methods = useForm<EditStepFormValues>();
  const { reset, handleSubmit } = methods;

  const dayRange = rawRule ? extractDayRange(rawRule) : null;
  const percentageInitial = rawRule ? extractPercentage(rawRule) : null;

  const hasDayRange = dayRange !== null;
  const hasPercentage = percentageInitial !== null;

  useEffect(() => {
    if (step && rawRule) {
      const dr = extractDayRange(rawRule);
      const pct = extractPercentage(rawRule);
      reset({
        name: rawRule.rule.name,
        description: rawRule.rule.description,
        priority: rawRule.rule.priority,
        isActive: rawRule.is_active === "active",
        ...(dr !== null && {
          dayRangeFrom: dr.from,
          dayRangeTo: dr.to ?? "",
        }),
        ...(pct !== null && { percentage: pct }),
      });
    }
  }, [step, rawRule, reset]);

  const onSubmit = handleSubmit(async (data: EditStepFormValues) => {
    if (!step || !rawRule) return;

    let updatedRule: RawRule = {
      ...rawRule.rule,
      id: rawRule.id,
      name: data.name,
      description: data.description,
      priority: data.priority,
    };

    if (hasDayRange && data.dayRangeFrom !== undefined) {
      const toInclusive =
        data.dayRangeTo === "" || data.dayRangeTo === undefined
          ? null
          : Number(data.dayRangeTo);
      updatedRule = injectDayRange(updatedRule, data.dayRangeFrom, toInclusive);
    }

    if (hasPercentage && data.percentage !== undefined) {
      updatedRule = injectPercentage(updatedRule, data.percentage);
    }

    const updatedStep: IncentiveStep = {
      ...step,
      name: data.name,
      description: data.description,
    };

    // is_active: pendiente definir endpoint (PUT vs PATCH)
    await saveIncentiveStep(country, updatedStep, updatedRule, rawRule.rule_id);
    onClose();
  });

  return { methods, onSubmit, isSaving, hasDayRange, hasPercentage };
};
